/**
 * Unified Analysis Cache Manager
 * 
 * Provides consistent localStorage-first caching for all analysis types:
 * - SEO Analysis
 * - Lighthouse Reports  
 * - Dashboard Stats
 * - Knowledge Graph Entities
 * 
 * Features:
 * - localStorage-first with database fallback
 * - Automatic quota management with LRU eviction
 * - Cross-device sync support
 * - Graceful degradation when localStorage unavailable
 * - Migration from legacy cache systems
 */

import { LighthouseReport } from '../types/lighthouse';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export type AnalysisType = 'seo' | 'lighthouse' | 'dashboard' | 'entities' | 'knowledge-graph' | 'semantic-analysis';

export interface CacheMetadata {
  cacheKey: string;
  url: string;
  projectId: string;
  timestamp: number;
  expiresAt: number;
  syncedWithDatabase: boolean;
  analysisId?: string;
  version: string;
}

export interface CachedAnalysis<T = any> {
  data: T;
  metadata: CacheMetadata;
}

export interface UnifiedCacheStats {
  version: string;
  userId: string;
  lastSync: number;
  totalSize: number;
  entriesCount: {
    seo: number;
    lighthouse: number;
    dashboard: number;
    entities: number;
  };
  quotaUsage: {
    used: number;
    available: number;
    percentage: number;
  };
}

export interface CacheOptions {
  ttl?: number;
  forceRefresh?: boolean;
  syncWithDatabase?: boolean;
}

// =============================================================================
// FEATURE FLAGS & CONFIGURATION
// =============================================================================

export interface UnifiedCacheConfig {
  enabled: boolean;
  enabledForAnalysisTypes?: AnalysisType[];
  enableMigration?: boolean;
  debugMode?: boolean;
  quota?: {
    maxTotalSize?: number;
    maxEntriesPerType?: Partial<Record<AnalysisType, number>>;
  };
}

// Helper to safely access environment variables (works in both Vite and Jest)
const getEnvVar = (key: string): string | undefined => {
  // Jest environment uses process.env
  if (typeof process !== 'undefined' && process.env?.[key] !== undefined) {
    return process.env[key];
  }
  // Vite browser environment uses import.meta.env
  if (typeof import.meta !== 'undefined' && import.meta.env?.[key] !== undefined) {
    return import.meta.env[key];
  }
  return undefined;
};

const isDevelopment = (): boolean => {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
    return process.env.NODE_ENV === 'development';
  }
  if (typeof import.meta !== 'undefined' && import.meta.env?.MODE) {
    return import.meta.env.MODE === 'development';
  }
  return false;
};

// Default configuration - can be overridden via environment variables or runtime config
// Kill switch: Set VITE_ENABLE_UNIFIED_CACHE=false to disable cache
const DEFAULT_CONFIG: UnifiedCacheConfig = {
  enabled: getEnvVar('VITE_ENABLE_UNIFIED_CACHE') !== 'false', // Enabled by default, can be disabled via env var
  enabledForAnalysisTypes: ['seo', 'lighthouse', 'dashboard', 'entities'], // Enable all types
  enableMigration: getEnvVar('VITE_ENABLE_CACHE_MIGRATION') !== 'false',
  debugMode: isDevelopment(),
  quota: {
    maxTotalSize: 4 * 1024 * 1024, // 4MB (browser quota is ~5MB)
    maxEntriesPerType: {
      seo: 50,
      lighthouse: 20,
      dashboard: 100,
      entities: 30
    }
  }
};

// =============================================================================
// UNIFIED CACHE MANAGER
// =============================================================================

class UnifiedAnalysisCacheManager {
  private readonly CACHE_PREFIX = 'rnk_unified_cache';
  private readonly VERSION = '3.0.0'; // SEO Analysis fix - Data Normalizer integration + validation
  private readonly STATS_KEY = `${this.CACHE_PREFIX}:stats`;
  
  private config: UnifiedCacheConfig = DEFAULT_CONFIG;
  
  // TTL Configuration (in milliseconds)
  private readonly DEFAULT_TTL: Record<AnalysisType, number> = {
    seo: 24 * 60 * 60 * 1000,        // 24 hours
    lighthouse: 24 * 60 * 60 * 1000, // 24 hours  
    dashboard: 5 * 60 * 1000,        // 5 minutes
    entities: 24 * 60 * 60 * 1000,   // 24 hours
    'knowledge-graph': 7 * 24 * 60 * 60 * 1000,  // 7 days (graphs change rarely)
    'semantic-analysis': 7 * 24 * 60 * 60 * 1000  // 7 days (semantic analysis stable)
  };

  // Storage Quota Management
  private readonly MAX_TOTAL_SIZE = 4 * 1024 * 1024; // 4MB safety limit (browser quota is ~5MB)
  private readonly MAX_ENTRY_SIZE = 350 * 1024; // 350KB max per entry - skip oversized payloads
  private readonly MAX_ENTRIES_PER_TYPE: Record<AnalysisType, number> = {
    seo: 50,        // ~160KB each = 8MB max
    lighthouse: 20, // ~400KB each = 8MB max
    dashboard: 100, // ~10KB each = 1MB max
    entities: 30,   // ~200KB each = 6MB max
    'knowledge-graph': 15,  // ~50-500KB each = 1.6MB max (20% of total quota)
    'semantic-analysis': 20  // ~50-200KB each = 4MB max
  };

  private isInitialized = false;

  // =============================================================================
  // CONFIGURATION & FEATURE FLAGS
  // =============================================================================

  /**
   * Update cache configuration (for runtime configuration changes)
   */
  updateConfig(newConfig: Partial<UnifiedCacheConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    if (this.config.debugMode) {
      console.log('🔧 UNIFIED CACHE: Configuration updated', {
        oldConfig: DEFAULT_CONFIG,
        newConfig: this.config,
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Check if unified cache is enabled globally
   */
  private isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Check if unified cache is enabled for specific analysis type
   */
  private isEnabledForType(type: AnalysisType): boolean {
    if (!this.isEnabled()) return false;
    
    const enabledTypes = this.config.enabledForAnalysisTypes;
    return !enabledTypes || enabledTypes.includes(type);
  }

  /**
   * Check if migration is enabled
   */
  private isMigrationEnabled(): boolean {
    return this.config.enableMigration !== false;
  }

  /**
   * Log debug information if debug mode is enabled
   */
  private debugLog(message: string, data?: any): void {
    if (this.config.debugMode) {
      console.log(`🔍 UNIFIED CACHE DEBUG: ${message}`, data);
    }
  }

  // =============================================================================
  // INITIALIZATION & UTILITIES
  // =============================================================================

  /**
   * Initialize cache manager and run startup tasks
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    // Prevent multiple initializations
    this.isInitialized = true;

    this.debugLog('Initializing cache manager', {
      version: this.VERSION,
      config: this.config,
      maxSize: `${this.MAX_TOTAL_SIZE / 1024 / 1024}MB`,
      timestamp: new Date().toISOString()
    });

    // Check if cache is enabled
    if (!this.isEnabled()) {
      console.log('ℹ️ UNIFIED CACHE: Cache is disabled by configuration');
      return;
    }

    console.log('🔧 UNIFIED CACHE: Initializing cache manager', {
      version: this.VERSION,
      enabled: this.config.enabled,
      enabledTypes: this.config.enabledForAnalysisTypes,
      migrationEnabled: this.config.enableMigration,
      maxSize: `${this.MAX_TOTAL_SIZE / 1024 / 1024}MB`,
      timestamp: new Date().toISOString()
    });

    try {
      // Check localStorage availability
      if (!this.isLocalStorageAvailable()) {
        console.warn('⚠️ UNIFIED CACHE: localStorage unavailable, using memory fallback');
        this.isInitialized = true;
        return;
      }

      // Initialize stats if not exists
      await this.initializeStats();

      // Migrate legacy cache data if enabled
      if (this.isMigrationEnabled()) {
        await this.migrateLegacyCache();
      } else {
        this.debugLog('Migration disabled by configuration');
      }

      // Clean outdated version entries (must run before expired cleanup)
      await this.cleanupOutdatedVersions();

      // Clean expired entries
      await this.cleanupExpiredEntries();

      // Enforce quota limits
      await this.enforceQuotaLimits();

      console.log('✅ UNIFIED CACHE: Cache manager initialized successfully', {
        config: this.config,
        stats: await this.getStats(),
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      console.error('❌ UNIFIED CACHE: Failed to initialize cache manager', {
        error: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        config: this.config
      });
      
      // Continue with memory-only fallback
    }
  }

  /**
   * Check if localStorage is available and functional
   */
  private isLocalStorageAvailable(): boolean {
    try {
      const test = '__cache_test__';
      localStorage.setItem(test, test);
      const retrieved = localStorage.getItem(test);
      localStorage.removeItem(test);
      return retrieved === test;
    } catch (error) {
      console.warn('❌ UNIFIED CACHE: localStorage unavailable', {
        error: error instanceof Error ? error.message : 'Unknown error',
        isPrivateBrowsing: this.detectPrivateBrowsing()
      });
      return false;
    }
  }

  /**
   * Detect private browsing mode
   */
  private detectPrivateBrowsing(): boolean {
    try {
      const test = '__private_test__';
      localStorage.setItem(test, 'test');
      localStorage.removeItem(test);
      return false;
    } catch (error) {
      return true;
    }
  }

  /**
   * Generate standardized cache key
   */
  private generateCacheKey(type: AnalysisType, projectId: string, url: string): string {
    // Use base64 encoding for URL to handle special characters
    const encodedUrl = btoa(url).replace(/[+/=]/g, (match) => {
      return { '+': '-', '/': '_', '=': '' }[match] || match;
    });
    
    return `${this.CACHE_PREFIX}:${type}:${projectId}:${encodedUrl}`;
  }

  /**
   * Calculate estimated size of data in bytes
   */
  private calculateDataSize(data: any): number {
    try {
      return new Blob([JSON.stringify(data)]).size;
    } catch (error) {
      // Fallback estimation
      return JSON.stringify(data).length * 2; // Rough UTF-16 estimation
    }
  }

  // =============================================================================
  // CORE CACHE OPERATIONS
  // =============================================================================

  /**
   * Get cached analysis data
   */
  async get<T>(
    type: AnalysisType, 
    projectId: string, 
    url: string, 
    options: CacheOptions = {}
  ): Promise<T | null> {
    await this.initialize();

    // Check if cache is enabled for this analysis type
    if (!this.isEnabledForType(type)) {
      this.debugLog(`Cache disabled for analysis type: ${type}`);
      return null;
    }

    if (!this.isLocalStorageAvailable()) {
      return null;
    }

    const cacheKey = this.generateCacheKey(type, projectId, url);

    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) {
        console.log('🔍 UNIFIED CACHE: Cache miss', {
          type, projectId, url, cacheKey,
          reason: 'no_entry_found'
        });
        return null;
      }

      const cachedAnalysis: CachedAnalysis<T> = JSON.parse(cached);
      const now = Date.now();

      // Check if cache has expired
      if (cachedAnalysis.metadata.expiresAt <= now) {
        console.log('⏰ UNIFIED CACHE: Cache expired, removing', {
          type, projectId, url, cacheKey,
          expiredAt: new Date(cachedAnalysis.metadata.expiresAt).toISOString(),
          ageMs: now - cachedAnalysis.metadata.timestamp
        });

        await this.invalidate(type, projectId, url);
        return null;
      }

      // 🔧 VERSION CHECK: Invalidate cache if version mismatch
      if (cachedAnalysis.metadata.version !== this.VERSION) {
        console.log('🔄 UNIFIED CACHE: Version mismatch, invalidating old cache', {
          type, projectId, url, cacheKey,
          cachedVersion: cachedAnalysis.metadata.version,
          currentVersion: this.VERSION,
          reason: 'cache_version_outdated'
        });

        await this.invalidate(type, projectId, url);
        return null;
      }

      // Check if force refresh requested
      if (options.forceRefresh) {
        console.log('🔄 UNIFIED CACHE: Force refresh requested, ignoring cache', {
          type, projectId, url, cacheKey
        });
        return null;
      }

      // 🔧 DATA VALIDATION: Ensure cached data has required fields
      const validation = this.validateCachedData(type, cachedAnalysis.data);
      if (!validation.valid) {
        console.warn('⚠️ UNIFIED CACHE: Incomplete data detected, invalidating cache', {
          type, projectId, url, cacheKey,
          missingFields: validation.missing,
          reason: 'Data validation failed - missing critical SEO fields',
          action: 'Invalidating to force fresh analysis'
        });

        await this.invalidate(type, projectId, url);
        return null;
      }

      console.log('✅ UNIFIED CACHE: Cache hit', {
        type, projectId, url, cacheKey,
        cachedAt: new Date(cachedAnalysis.metadata.timestamp).toISOString(),
        expiresAt: new Date(cachedAnalysis.metadata.expiresAt).toISOString(),
        ageMs: now - cachedAnalysis.metadata.timestamp
      });

      // Update stats
      await this.updateStats('hit', type);

      return cachedAnalysis.data;

    } catch (error) {
      console.error('❌ UNIFIED CACHE: Failed to get cached data', {
        type, projectId, url, cacheKey,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      
      // Remove corrupted entry
      localStorage.removeItem(cacheKey);
      return null;
    }
  }

  /**
   * Set cached analysis data
   */
  async set<T>(
    type: AnalysisType,
    projectId: string, 
    url: string,
    data: T,
    options: CacheOptions = {}
  ): Promise<void> {
    await this.initialize();

    // Check if cache is enabled for this analysis type
    if (!this.isEnabledForType(type)) {
      this.debugLog(`Cache disabled for analysis type: ${type}`);
      return;
    }

    if (!this.isLocalStorageAvailable()) {
      return;
    }

    const cacheKey = this.generateCacheKey(type, projectId, url);
    const now = Date.now();
    const ttl = options.ttl || this.DEFAULT_TTL[type];

    const cachedAnalysis: CachedAnalysis<T> = {
      data,
      metadata: {
        cacheKey,
        url,
        projectId,
        timestamp: now,
        expiresAt: now + ttl,
        syncedWithDatabase: options.syncWithDatabase || false,
        version: this.VERSION
      }
    };

    try {
      // Check if we have space before adding
      const dataSize = this.calculateDataSize(cachedAnalysis);

      // 🚫 SKIP OVERSIZED PAYLOADS: Don't cache entries larger than MAX_ENTRY_SIZE
      if (dataSize > this.MAX_ENTRY_SIZE) {
        console.warn('⏭️ UNIFIED CACHE: Skipping oversized payload', {
          type, projectId, url, cacheKey,
          dataSize: `${Math.round(dataSize / 1024)}KB`,
          maxEntrySize: `${Math.round(this.MAX_ENTRY_SIZE / 1024)}KB`,
          reason: 'payload_exceeds_max_entry_size'
        });
        return; // Skip caching this entry entirely
      }

      await this.ensureStorageSpace(dataSize);

      // Store the data
      localStorage.setItem(cacheKey, JSON.stringify(cachedAnalysis));

      console.log('💾 UNIFIED CACHE: Data cached successfully', {
        type, projectId, url, cacheKey,
        dataSize: `${Math.round(dataSize / 1024)}KB`,
        ttl: `${Math.round(ttl / 1000 / 60)}min`,
        expiresAt: new Date(cachedAnalysis.metadata.expiresAt).toISOString()
      });

      // Update stats
      await this.updateStats('set', type, dataSize);

    } catch (error) {
      console.error('❌ UNIFIED CACHE: Failed to cache data', {
        type, projectId, url, cacheKey,
        error: error instanceof Error ? error.message : 'Unknown error',
        errorName: error instanceof Error ? error.name : undefined
      });

      // Handle quota exceeded error - cleanup but DO NOT retry with same payload
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        await this.handleQuotaExceeded();
        // 🚫 NO RETRY: Don't retry with the same oversized payload - it will just fail again
        console.warn('⚠️ UNIFIED CACHE: Quota exceeded - cleaned up but NOT retrying (payload likely too large)', {
          type, projectId, url, cacheKey,
          reason: 'quota_exceeded_no_retry'
        });
        return;
      }
    }
  }

  /**
   * Invalidate cached data
   */
  async invalidate(
    type: AnalysisType,
    projectId: string,
    url?: string
  ): Promise<void> {
    await this.initialize();

    if (!this.isLocalStorageAvailable()) {
      return;
    }

    try {
      if (url) {
        // Invalidate specific entry
        const cacheKey = this.generateCacheKey(type, projectId, url);
        localStorage.removeItem(cacheKey);
        
        console.log('🗑️ UNIFIED CACHE: Invalidated specific entry', {
          type, projectId, url, cacheKey,
          stackTrace: new Error().stack
        });
      } else {
        // Invalidate all entries of type for project
        const prefix = `${this.CACHE_PREFIX}:${type}:${projectId}:`;
        const keysToRemove: string[] = [];
        
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(prefix)) {
            keysToRemove.push(key);
          }
        }

        keysToRemove.forEach(key => localStorage.removeItem(key));

        console.log('🗑️ UNIFIED CACHE: Invalidated project entries', {
          type, projectId, entriesRemoved: keysToRemove.length
        });
      }

      // Update stats
      await this.updateStats('invalidate', type);

    } catch (error) {
      console.error('❌ UNIFIED CACHE: Failed to invalidate cache', {
        type, projectId, url,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }

  // =============================================================================
  // QUOTA MANAGEMENT
  // =============================================================================

  /**
   * Ensure sufficient storage space is available
   */
  private async ensureStorageSpace(requiredSize: number): Promise<void> {
    const stats = await this.getStats();
    const availableSpace = this.MAX_TOTAL_SIZE - stats.totalSize;

    if (availableSpace >= requiredSize) {
      return; // Sufficient space available
    }

    console.log('🧹 UNIFIED CACHE: Insufficient space, starting cleanup', {
      requiredSize: `${Math.round(requiredSize / 1024)}KB`,
      availableSpace: `${Math.round(availableSpace / 1024)}KB`,
      totalSize: `${Math.round(stats.totalSize / 1024)}KB`,
      maxSize: `${Math.round(this.MAX_TOTAL_SIZE / 1024)}KB`
    });

    // Calculate space to free (required + 10% buffer)
    const spaceToFree = requiredSize - availableSpace + (this.MAX_TOTAL_SIZE * 0.1);
    await this.evictOldestEntries(spaceToFree);
  }

  /**
   * Evict oldest cache entries to free space
   */
  private async evictOldestEntries(targetSize: number): Promise<void> {
    const entries: Array<{
      key: string;
      timestamp: number;
      size: number;
      type: AnalysisType;
    }> = [];

    // Collect all cache entries with metadata
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(this.CACHE_PREFIX)) continue;
      if (key === this.STATS_KEY) continue;

      try {
        const data = localStorage.getItem(key);
        if (data) {
          const parsed: CachedAnalysis = JSON.parse(data);
          const size = this.calculateDataSize(parsed);
          const type = this.extractTypeFromKey(key);
          
          if (type) {
            entries.push({
              key,
              timestamp: parsed.metadata.timestamp,
              size,
              type
            });
          }
        }
      } catch (error) {
        // Remove corrupted entry
        localStorage.removeItem(key);
      }
    }

    // Sort by timestamp (oldest first)
    entries.sort((a, b) => a.timestamp - b.timestamp);

    let freedSize = 0;
    let removedCount = 0;

    for (const entry of entries) {
      if (freedSize >= targetSize) break;

      localStorage.removeItem(entry.key);
      freedSize += entry.size;
      removedCount++;

      console.log('🗑️ UNIFIED CACHE: Evicted old entry', {
        key: entry.key,
        type: entry.type,
        size: `${Math.round(entry.size / 1024)}KB`,
        age: `${Math.round((Date.now() - entry.timestamp) / 1000 / 60)}min`
      });
    }

    console.log('✅ UNIFIED CACHE: Cleanup completed', {
      removedCount,
      freedSize: `${Math.round(freedSize / 1024)}KB`,
      targetSize: `${Math.round(targetSize / 1024)}KB`
    });
  }

  /**
   * Extract analysis type from cache key
   */
  private extractTypeFromKey(key: string): AnalysisType | null {
    const parts = key.split(':');
    if (parts.length >= 3) {
      const type = parts[2] as AnalysisType;
      if (['seo', 'lighthouse', 'dashboard', 'entities', 'knowledge-graph'].includes(type)) {
        return type;
      }
    }
    return null;
  }

  /**
   * Handle quota exceeded error
   */
  private async handleQuotaExceeded(): Promise<void> {
    console.warn('⚠️ UNIFIED CACHE: Storage quota exceeded, performing emergency cleanup');
    
    // Free 25% of total cache space
    const targetSize = this.MAX_TOTAL_SIZE * 0.25;
    await this.evictOldestEntries(targetSize);
  }

  /**
   * Enforce per-type entry limits
   */
  private async enforceQuotaLimits(): Promise<void> {
    for (const type of Object.keys(this.MAX_ENTRIES_PER_TYPE) as AnalysisType[]) {
      await this.enforceTypeLimit(type);
    }
  }

  /**
   * Enforce entry limit for specific analysis type
   */
  private async enforceTypeLimit(type: AnalysisType): Promise<void> {
    const maxEntries = this.MAX_ENTRIES_PER_TYPE[type];
    const entries: Array<{ key: string; timestamp: number }> = [];

    // Collect entries of this type
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(`${this.CACHE_PREFIX}:${type}:`)) continue;

      try {
        const data = localStorage.getItem(key);
        if (data) {
          const parsed: CachedAnalysis = JSON.parse(data);
          entries.push({
            key,
            timestamp: parsed.metadata.timestamp
          });
        }
      } catch (error) {
        // Remove corrupted entry
        localStorage.removeItem(key);
      }
    }

    if (entries.length <= maxEntries) return;

    // Sort by timestamp and remove excess
    entries.sort((a, b) => a.timestamp - b.timestamp);
    const toRemove = entries.slice(0, entries.length - maxEntries);

    toRemove.forEach(entry => {
      localStorage.removeItem(entry.key);
    });

    if (toRemove.length > 0) {
      console.log('🗑️ UNIFIED CACHE: Enforced type limit', {
        type,
        maxEntries,
        entriesRemoved: toRemove.length,
        entriesRemaining: maxEntries
      });
    }
  }

  // =============================================================================
  // STATISTICS & MONITORING
  // =============================================================================

  /**
   * Initialize cache statistics
   */
  private async initializeStats(): Promise<void> {
    try {
      const existingStats = localStorage.getItem(this.STATS_KEY);
      if (!existingStats) {
        const initialStats: UnifiedCacheStats = {
          version: this.VERSION,
          userId: this.extractUserIdFromContext(),
          lastSync: 0,
          totalSize: 0,
          entriesCount: {
            seo: 0,
            lighthouse: 0,
            dashboard: 0,
            entities: 0
          },
          quotaUsage: {
            used: 0,
            available: this.MAX_TOTAL_SIZE,
            percentage: 0
          }
        };

        localStorage.setItem(this.STATS_KEY, JSON.stringify(initialStats));
      }
    } catch (error) {
      console.warn('⚠️ UNIFIED CACHE: Failed to initialize stats', error);
    }
  }

  /**
   * Update cache statistics
   */
  private async updateStats(
    operation: 'hit' | 'miss' | 'set' | 'invalidate',
    type: AnalysisType,
    dataSize = 0
  ): Promise<void> {
    // Implementation would update hit rates, sizes, etc.
    // Keeping simple for now to avoid localStorage overhead
  }

  /**
   * Get current cache statistics
   */
  async getStats(): Promise<UnifiedCacheStats> {
    await this.initialize();

    if (!this.isLocalStorageAvailable()) {
      return {
        version: this.VERSION,
        userId: 'anonymous',
        lastSync: 0,
        totalSize: 0,
        entriesCount: { seo: 0, lighthouse: 0, dashboard: 0, entities: 0 },
        quotaUsage: { used: 0, available: 0, percentage: 0 }
      };
    }

    try {
      // Calculate real-time stats
      const entriesCount = { seo: 0, lighthouse: 0, dashboard: 0, entities: 0 };
      let totalSize = 0;

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(this.CACHE_PREFIX)) continue;
        if (key === this.STATS_KEY) continue;

        try {
          const data = localStorage.getItem(key);
          if (data) {
            const size = this.calculateDataSize(JSON.parse(data));
            totalSize += size;

            const type = this.extractTypeFromKey(key);
            if (type) {
              entriesCount[type]++;
            }
          }
        } catch (error) {
          // Skip corrupted entries
        }
      }

      return {
        version: this.VERSION,
        userId: this.extractUserIdFromContext(),
        lastSync: Date.now(),
        totalSize,
        entriesCount,
        quotaUsage: {
          used: totalSize,
          available: this.MAX_TOTAL_SIZE - totalSize,
          percentage: Math.round((totalSize / this.MAX_TOTAL_SIZE) * 100)
        }
      };

    } catch (error) {
      console.error('❌ UNIFIED CACHE: Failed to get stats', error);
      return {
        version: this.VERSION,
        userId: 'anonymous',
        lastSync: 0,
        totalSize: 0,
        entriesCount: { seo: 0, lighthouse: 0, dashboard: 0, entities: 0 },
        quotaUsage: { used: 0, available: this.MAX_TOTAL_SIZE, percentage: 0 }
      };
    }
  }

  // =============================================================================
  // MIGRATION & CLEANUP
  // =============================================================================

  /**
   * Migrate data from legacy cache systems
   */
  private async migrateLegacyCache(): Promise<void> {
    console.log('🔄 UNIFIED CACHE: Starting legacy cache migration');

    try {
      await this.migrateEntityCache();
      // TODO: Add other legacy cache migrations as needed
      
      console.log('✅ UNIFIED CACHE: Legacy cache migration completed');
    } catch (error) {
      console.error('❌ UNIFIED CACHE: Legacy cache migration failed', error);
    }
  }

  /**
   * Migrate from existing entityCache.ts system
   */
  private async migrateEntityCache(): Promise<void> {
    const entityKeys = Object.keys(localStorage).filter(key => 
      key.startsWith('rnk_entity_cache:')
    );

    if (entityKeys.length === 0) {
      console.log('ℹ️ UNIFIED CACHE: No entity cache entries to migrate');
      return;
    }

    let migrated = 0;
    let skipped = 0;
    const migrationMap = new Map<string, string[]>(); // domain -> migrated keys

    for (const key of entityKeys) {
      try {
        const data = localStorage.getItem(key);
        if (!data) {
          skipped++;
          continue;
        }

        const entityData = JSON.parse(data);
        
        // Extract domain and other info from old format
        const domain = entityData.metadata?.domain;
        if (!domain) {
          console.warn('⚠️ UNIFIED CACHE: Skipping entity cache entry without domain', { key });
          skipped++;
          continue;
        }

        // Try to extract project ID from current auth context or use migration placeholder
        const projectId = this.extractProjectIdFromContext() || 'migration-pending';
        
        // Construct URL from metadata
        const sourceUrl = entityData.metadata?.sourceUrl || `https://${domain}`;
        
        // Preserve original metadata for reference
        const migratedData = {
          entities: entityData.entities || [],
          relationships: entityData.relationships || [],
          topics: entityData.topics || [],
          // Add migration metadata
          _migrationInfo: {
            originalKey: key,
            migratedAt: Date.now(),
            originalTimestamp: entityData.metadata?.timestamp,
            originalHash: entityData.metadata?.contentHash
          }
        };

        // Use consistent cache options
        await this.set('entities', projectId, sourceUrl, migratedData, {
          ttl: this.DEFAULT_TTL.entities,
          syncWithDatabase: false // Don't sync migrated data immediately
        });

        // Track migration by domain
        if (!migrationMap.has(domain)) {
          migrationMap.set(domain, []);
        }
        migrationMap.get(domain)!.push(key);

        // Remove old format entry only after successful migration
        localStorage.removeItem(key);
        migrated++;

        console.log('🔄 UNIFIED CACHE: Migrated entity cache entry', {
          oldKey: key,
          newProjectId: projectId,
          domain,
          sourceUrl,
          entitiesCount: entityData.entities?.length || 0,
          relationshipsCount: entityData.relationships?.length || 0,
          topicsCount: entityData.topics?.length || 0
        });

      } catch (error) {
        console.warn('⚠️ UNIFIED CACHE: Failed to migrate entity cache entry', {
          key,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        
        // Don't remove corrupted entries during migration - let them be cleaned up separately
        skipped++;
      }
    }

    // Clean up old index files
    await this.cleanupLegacyIndexes();

    if (migrated > 0) {
      console.log('✅ UNIFIED CACHE: Entity cache migration completed', {
        entriesMigrated: migrated,
        entriesSkipped: skipped,
        domainsMigrated: migrationMap.size,
        migrationSummary: Array.from(migrationMap.entries()).map(([domain, keys]) => ({
          domain,
          entriesCount: keys.length
        }))
      });
    }
  }

  /**
   * Try to extract current project ID from context (auth, URL, etc.)
   */
  private extractProjectIdFromContext(): string | null {
    try {
      // Try to get from URL params first
      const urlParams = new URLSearchParams(window.location.search);
      const projectId = urlParams.get('projectId');
      if (projectId) return projectId;

      // Try to get from localStorage auth context
      const authData = localStorage.getItem('rnk_auth');
      if (authData) {
        const auth = JSON.parse(authData);
        if (auth.currentProject?.id) {
          return auth.currentProject.id;
        }
      }

      // Try alternative auth key
      const currentProject = localStorage.getItem('rnk_current_project');
      if (currentProject) {
        const project = JSON.parse(currentProject);
        if (project.id) return project.id;
      }

      return null;
    } catch (error) {
      console.warn('Failed to extract project ID from context:', error);
      return null;
    }
  }

  /**
   * Try to extract current user ID from localStorage
   */
  private extractUserIdFromContext(): string {
    try {
      // Check localStorage for user data
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || user.userId || 'anonymous';
      }

      // Check sessionStorage as fallback
      const sessionUserStr = sessionStorage.getItem('user');
      if (sessionUserStr) {
        const user = JSON.parse(sessionUserStr);
        return user.id || user.userId || 'anonymous';
      }

      return 'anonymous';
    } catch (error) {
      return 'anonymous';
    }
  }

  /**
   * Clean up legacy entity cache index files
   */
  private async cleanupLegacyIndexes(): Promise<void> {
    const indexKeys = Object.keys(localStorage).filter(key => 
      key.startsWith('rnk_entity_cache_index:')
    );

    if (indexKeys.length === 0) return;

    let cleaned = 0;
    for (const key of indexKeys) {
      try {
        localStorage.removeItem(key);
        cleaned++;
      } catch (error) {
        console.warn('Failed to clean legacy index:', key);
      }
    }

    if (cleaned > 0) {
      console.log('🧹 UNIFIED CACHE: Cleaned up legacy index files', {
        indexesRemoved: cleaned
      });
    }
  }

  /**
   * Clean up expired cache entries
   */
  private async cleanupExpiredEntries(): Promise<void> {
    const now = Date.now();
    let cleanedCount = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(this.CACHE_PREFIX)) continue;
      if (key === this.STATS_KEY) continue;

      try {
        const data = localStorage.getItem(key);
        if (data) {
          const cached: CachedAnalysis = JSON.parse(data);
          if (cached.metadata.expiresAt <= now) {
            localStorage.removeItem(key);
            cleanedCount++;
          }
        }
      } catch (error) {
        // Remove corrupted entry
        localStorage.removeItem(key);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      console.log('🧹 UNIFIED CACHE: Cleaned expired entries', {
        entriesRemoved: cleanedCount
      });
    }
  }

  /**
   * Cleanup cache entries with outdated versions
   */
  private async cleanupOutdatedVersions(): Promise<void> {
    let cleanedCount = 0;
    const outdatedVersions: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(this.CACHE_PREFIX)) continue;
      if (key === this.STATS_KEY) continue;

      try {
        const data = localStorage.getItem(key);
        if (data) {
          const cached: CachedAnalysis = JSON.parse(data);
          // Check if version exists and matches current version
          if (cached.metadata.version && cached.metadata.version !== this.VERSION) {
            localStorage.removeItem(key);
            outdatedVersions.push(cached.metadata.version);
            cleanedCount++;
          }
          // Also remove entries without version (very old format)
          else if (!cached.metadata.version) {
            localStorage.removeItem(key);
            outdatedVersions.push('unknown');
            cleanedCount++;
          }
        }
      } catch (error) {
        // Remove corrupted entry
        localStorage.removeItem(key);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      console.log('🔄 UNIFIED CACHE: Cleaned outdated version entries', {
        entriesRemoved: cleanedCount,
        outdatedVersions: [...new Set(outdatedVersions)],
        currentVersion: this.VERSION,
        reason: 'Data structure updated - old caches invalidated for fresh data'
      });
    }
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  /**
   * Validate cached SEO data has required fields
   * Prevents serving incomplete data that would cause UI to show "Missing"
   */
  private validateCachedData(type: AnalysisType, data: any): { valid: boolean; missing: string[] } {
    // Only validate SEO analysis - other types don't have these fields
    if (type !== 'seo') {
      return { valid: true, missing: [] };
    }

    // Required fields for SEO analysis - must have at least one of these populated
    const criticalFields = ['page_title', 'meta_description'];
    const recommendedFields = ['overall_score', 'performance_score', 'seo_score'];

    const missingCritical = criticalFields.filter(field => {
      const value = data?.[field];
      return !value || value === '' || value === 'Missing' || value === 'N/A';
    });

    const missingRecommended = recommendedFields.filter(field => {
      const value = data?.[field];
      return value === undefined || value === null || value === 0;
    });

    // Invalid if missing ANY critical fields or ALL recommended fields
    const valid = missingCritical.length === 0 && missingRecommended.length < recommendedFields.length;

    return {
      valid,
      missing: [...missingCritical, ...missingRecommended]
    };
  }

  /**
   * Clear all cache data (for testing/debugging)
   */
  async clearAll(): Promise<void> {
    if (!this.isLocalStorageAvailable()) return;

    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));

    console.log('🗑️ UNIFIED CACHE: All cache data cleared', {
      entriesRemoved: keysToRemove.length
    });
  }

  /**
   * Check if data exists in cache
   */
  async has(type: AnalysisType, projectId: string, url: string): Promise<boolean> {
    const data = await this.get(type, projectId, url);
    return data !== null;
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

// Singleton instance
let _unifiedCacheInstance: UnifiedAnalysisCacheManager | null = null;

// Get or create singleton instance
export const unifiedCache = (() => {
  if (!_unifiedCacheInstance) {
    _unifiedCacheInstance = new UnifiedAnalysisCacheManager();
  }
  return _unifiedCacheInstance;
})();

// Configuration helpers
export const unifiedCacheConfig = {
  /**
   * Enable unified cache for specific analysis types
   */
  enableForTypes(types: AnalysisType[]): void {
    unifiedCache.updateConfig({
      enabled: true,
      enabledForAnalysisTypes: types
    });
  },

  /**
   * Enable unified cache for all analysis types (full rollout)
   */
  enableAll(): void {
    unifiedCache.updateConfig({
      enabled: true,
      enabledForAnalysisTypes: ['seo', 'lighthouse', 'dashboard', 'entities']
    });
  },

  /**
   * Disable unified cache completely
   */
  disable(): void {
    unifiedCache.updateConfig({
      enabled: false
    });
  },

  /**
   * Enable debug mode
   */
  enableDebug(): void {
    unifiedCache.updateConfig({
      debugMode: true
    });
  },

  /**
   * Configure quota settings
   */
  setQuotaLimits(quota: {
    maxTotalSize?: number;
    maxEntriesPerType?: Partial<Record<AnalysisType, number>>;
  }): void {
    unifiedCache.updateConfig({
      quota
    });
  }
};

// Export class for testing
export { UnifiedAnalysisCacheManager };

// Export types
export type {
  AnalysisType,
  CacheMetadata,
  CachedAnalysis,
  UnifiedCacheStats,
  CacheOptions,
  UnifiedCacheConfig
};